import { useEffect, useRef, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { api, ApiError } from '@orisini/ui/lib/api'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import { mediaApi, type MediaDto, type PinDto } from '../api'
import { TEXT_LIMITS } from '../constants'

interface BoardOption {
  id: string
  name: string
}

interface BulkRow {
  key: string
  media: MediaDto | null
  uploading: boolean
  progress: number
  filename: string
  title: string
  description: string
  link: string
  boardId: string
}

/**
 * Input massal: jatuhkan banyak file -> tiap file jadi satu baris draft ->
 * lengkapi di tabel -> masukkan semua ke antrean sekali klik.
 *
 * batchId dibuat sekali per sesi halaman: retry jaringan pada tombol
 * "Masukkan antrean" tidak akan menduplikasi pin (idempoten di server).
 */
export default function BulkPage() {
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [boards, setBoards] = useState<BoardOption[]>([])
  const [rows, setRows] = useState<BulkRow[]>([])
  const [submitting, setSubmitting] = useState(false)
  const batchId = useRef(`web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    accountsApi
      .list()
      .then((res) => {
        setAccounts(res.accounts)
        if (res.accounts.length) setAccountId(res.accounts[0].id)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!accountId) return
    api
      .get<{ boards: BoardOption[] }>(`/accounts/${accountId}/boards`)
      .then((res) => setBoards(res.boards))
      .catch(() => setBoards([]))
  }, [accountId])

  function addFiles(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      const key = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const row: BulkRow = {
        key,
        media: null,
        uploading: true,
        progress: 0,
        filename: file.name,
        title: file.name.replace(/\.[^.]+$/, '').slice(0, TEXT_LIMITS.TITLE),
        description: '',
        link: '',
        boardId: boards[0]?.id ?? '',
      }
      setRows((r) => [...r, row])

      mediaApi
        .upload(file, (pct) =>
          setRows((r) => r.map((x) => (x.key === key ? { ...x, progress: pct } : x))),
        )
        .then((media) =>
          setRows((r) => r.map((x) => (x.key === key ? { ...x, media, uploading: false } : x))),
        )
        .catch((err: unknown) => {
          toast.error(
            `${file.name}: ${err instanceof ApiError ? err.message : 'upload failed'}`,
          )
          setRows((r) => r.filter((x) => x.key !== key))
        })
    }
  }

  function patchRow(key: string, patch: Partial<BulkRow>) {
    setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)))
  }

  const ready = rows.filter((r) => r.media && !r.uploading)
  const incomplete = ready.filter((r) => !r.description.trim() || !r.boardId)

  async function submit() {
    if (!ready.length) return
    setSubmitting(true)
    try {
      const res = await api.post<{ pins: PinDto[]; enqueueErrors?: Array<{ error: string }> }>(
        '/pins/bulk',
        {
          accountId,
          batchId: batchId.current,
          enqueue: true,
          items: ready.map((r) => ({
            mediaId: r.media!.id,
            title: r.title || undefined,
            description: r.description || undefined,
            link: r.link || undefined,
            boardId: r.boardId || undefined,
          })),
        },
      )
      const failed = res.enqueueErrors?.length ?? 0
      toast.success(
        `${res.pins.length - failed} pins queued${failed ? `, ${failed} failed (${res.enqueueErrors![0].error})` : ''}`,
      )
      setRows([])
      batchId.current = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t submit the batch')
    } finally {
      setSubmitting(false)
    }
  }

  function applyBoardToAll(boardId: string) {
    setRows((r) => r.map((x) => ({ ...x, boardId })))
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bulk input</h1>
          <p className="text-sm text-muted-foreground">
            Drop many images at once, fill them in, then queue them all
          </p>
        </div>
        {accounts.length > 1 && (
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        )}
      </header>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          addFiles(e.dataTransfer.files)
        }}
        className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 text-center"
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <Upload className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Drag files here, or</p>
        <Button variant="outline" onClick={() => fileRef.current?.click()}>
          Select files
        </Button>
      </div>

      {rows.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">{rows.length} files</CardTitle>
            {boards.length > 0 && (
              <select
                onChange={(e) => e.target.value && applyBoardToAll(e.target.value)}
                defaultValue=""
                className="h-9 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="">Apply board to all…</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row) => (
              <div key={row.key} className="flex items-start gap-3 rounded-md border p-3">
                <div className="size-16 shrink-0 overflow-hidden rounded bg-muted">
                  {row.uploading ? (
                    <div className="flex size-full flex-col items-center justify-center gap-1">
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {row.progress}%
                      </span>
                    </div>
                  ) : row.media?.previewUrl ? (
                    <img src={row.media.previewUrl} alt="" className="size-full object-cover" />
                  ) : (
                    <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
                      {row.media?.kind}
                    </div>
                  )}
                </div>

                <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="Title"
                    value={row.title}
                    maxLength={TEXT_LIMITS.TITLE}
                    onChange={(e) => patchRow(row.key, { title: e.target.value })}
                  />
                  <select
                    value={row.boardId}
                    onChange={(e) => patchRow(row.key, { boardId: e.target.value })}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Select board…</option>
                    {boards.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="sm:col-span-2"
                    placeholder="Description (required)"
                    value={row.description}
                    maxLength={TEXT_LIMITS.DESCRIPTION}
                    onChange={(e) => patchRow(row.key, { description: e.target.value })}
                  />
                  <Input
                    className="sm:col-span-2"
                    placeholder="Destination link (optional)"
                    value={row.link}
                    onChange={(e) => patchRow(row.key, { link: e.target.value })}
                  />
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground"
                  onClick={() => setRows((r) => r.filter((x) => x.key !== row.key))}
                >
                  Delete
                </Button>
              </div>
            ))}

            <div className="flex items-center justify-between border-t pt-4">
              <p className="text-xs text-muted-foreground">
                {incomplete.length > 0
                  ? `${incomplete.length} rows incomplete (description & board required)`
                  : 'All rows ready'}
              </p>
              <Button
                onClick={() => void submit()}
                disabled={submitting || ready.length === 0 || incomplete.length > 0}
              >
                {submitting ? <Loader2 className="animate-spin" /> : null}
                Queue {ready.length} pins
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
